/** Invite sharing. See scope.md §7. */

import { type Translate, translator } from '@garbage/shared';

const english = translator('en-GB');

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'unavailable';

export type ShareDeps = {
  share?: (data: { title: string; text: string; url: string }) => Promise<void>;
  writeText?: (text: string) => Promise<void>;
};

/** In the sender's language — they are the one who knows who they are inviting. */
export function inviteMessage(code: string, url: string, t: Translate = english): { title: string; text: string; url: string } {
  return {
    title: 'Garbage',
    text: t('invite.message', { code }),
    url,
  };
}

/**
 * Use the native share sheet where there is one, fall back to the clipboard, and
 * report which happened so the interface can say something honest.
 */
export async function shareInvite(
  code: string, url: string, deps: ShareDeps = {}, t: Translate = english,
): Promise<ShareOutcome> {
  const payload = inviteMessage(code, url, t);
  const share = deps.share
    ?? (typeof navigator !== 'undefined' && navigator.share
      ? navigator.share.bind(navigator)
      : undefined);

  if (share) {
    try {
      await share(payload);
      return 'shared';
    } catch (error) {
      // The share sheet throws AbortError when the user backs out — not a failure.
      if (error instanceof Error && error.name === 'AbortError') return 'cancelled';
    }
  }

  const writeText = deps.writeText
    ?? (typeof navigator !== 'undefined' && navigator.clipboard
      ? navigator.clipboard.writeText.bind(navigator.clipboard)
      : undefined);

  if (writeText) {
    try {
      await writeText(url);
      return 'copied';
    } catch {
      return 'unavailable';
    }
  }
  return 'unavailable';
}

/** Deep links for the two apps a parent is most likely to use. */
export function whatsappLink(code: string, url: string, t: Translate = english): string {
  const { text } = inviteMessage(code, url, t);
  return `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
}

export function smsLink(code: string, url: string, t: Translate = english): string {
  const { text } = inviteMessage(code, url, t);
  // `&body=` is the form both iOS and Android accept after a `?`.
  return `sms:?&body=${encodeURIComponent(`${text} ${url}`)}`;
}
